/**
 * useWorkflowSimulation (P4.T10)
 *
 * RQ-based hook coordinating four mutations against the P4 simulate
 * endpoints (backend Tasks 6-8, backend/app/routers/workflows.py):
 *
 *   start       → POST   /api/workflows/:workflowId/simulate
 *   sendMessage → POST   /api/workflows/simulations/:instanceId/message
 *   fireEvent   → POST   /api/workflows/simulations/:instanceId/events
 *   reset       → DELETE /api/workflows/simulations/:instanceId  (204)
 *
 * Derives a 5-state state machine from mutation flight + the session's
 * agenda.paused projection + turn status:
 *
 *   idle | running | ready | paused | terminal
 *
 * Mirrors useWorkflowTest.ts's shape: apiClient.post<T> for JSON POSTs,
 * raw apiClient.fetch + response.ok check for the 204 DELETE, useState
 * locals + an applyTurn callback shared by sendMessage/fireEvent, and the
 * state machine derived via useMemo.
 */
import { useCallback, useMemo, useRef, useState } from 'react'
import { useMutation, type UseMutationResult } from '@tanstack/react-query'

import { apiClient } from '@/services/apiClient'
import type {
  SimulateAgendaState,
  SimulateEventArgs,
  SimulateMessageArgs,
  SimulateSessionResponse,
  SimulateStartArgs,
  SimulateTranscriptEntry,
  SimulateTurnResponse,
} from '@/types/workflowSimulate'

export type SimulationState = 'idle' | 'running' | 'ready' | 'paused' | 'terminal'

const TERMINAL_STATUSES = ['completed', 'failed', 'abandoned']

export type UseWorkflowSimulationResult = {
  start: UseMutationResult<SimulateSessionResponse, Error, SimulateStartArgs>
  sendMessage: UseMutationResult<SimulateTurnResponse, Error, SimulateMessageArgs>
  fireEvent: UseMutationResult<SimulateTurnResponse, Error, SimulateEventArgs>
  reset: UseMutationResult<void, Error, void>
  session: SimulateSessionResponse | null
  agenda: SimulateAgendaState | null
  transcript: SimulateTranscriptEntry[]
  status: string | null
  paused: SimulateAgendaState['paused']
  lastError: string | null
  state: SimulationState
}

export function useWorkflowSimulation({
  workflowId,
}: {
  workflowId: string
}): UseWorkflowSimulationResult {
  const [session, setSession] = useState<SimulateSessionResponse | null>(null)
  const [agenda, setAgenda] = useState<SimulateAgendaState | null>(null)
  const [transcript, setTranscript] = useState<SimulateTranscriptEntry[]>([])
  const [status, setStatus] = useState<string | null>(null)
  const [lastError, setLastError] = useState<string | null>(null)

  // Mirrors `session` for synchronous reads inside mutationFn closures.
  // A caller (the rail component, or this hook's own test) may await
  // `start.mutateAsync` and immediately await `sendMessage.mutateAsync`
  // without an intervening render/flush — `sendMessage`'s mutationFn
  // closure would otherwise still see the pre-`start` render's `session`
  // (null) because React hasn't committed the `setSession` update from
  // `start`'s `onSuccess` yet. Ref writes are synchronous and are not
  // subject to that render-commit lag, so every mutationFn reads
  // `sessionRef.current` instead of the `session` state variable.
  const sessionRef = useRef<SimulateSessionResponse | null>(null)

  const applyTurn = useCallback((turn: SimulateTurnResponse) => {
    setAgenda(turn.agenda)
    setStatus(turn.status)
    setLastError(turn.error)
    setTranscript((prev) => [
      ...prev,
      {
        role: 'assistant',
        content: turn.reply_text,
        text_channel_sends: turn.text_channel_sends,
        error: turn.error,
      },
    ])
  }, [])

  const start = useMutation<SimulateSessionResponse, Error, SimulateStartArgs>({
    mutationFn: async (args) =>
      await apiClient.post<SimulateSessionResponse>(
        `/api/workflows/${workflowId}/simulate`,
        args,
      ),
    onSuccess: (data) => {
      sessionRef.current = data
      setSession(data)
      setAgenda(data.agenda)
      setTranscript([])
      setStatus('active')
      setLastError(null)
    },
  })

  const sendMessage = useMutation<SimulateTurnResponse, Error, SimulateMessageArgs>({
    mutationFn: async (args) => {
      const currentSession = sessionRef.current
      if (!currentSession) throw new Error('No active simulation')
      setTranscript((prev) => [...prev, { role: 'user', content: args.message }])
      return await apiClient.post<SimulateTurnResponse>(
        `/api/workflows/simulations/${currentSession.instance_id}/message`,
        args,
      )
    },
    onSuccess: applyTurn,
  })

  const fireEvent = useMutation<SimulateTurnResponse, Error, SimulateEventArgs>({
    mutationFn: async (args) => {
      const currentSession = sessionRef.current
      if (!currentSession) throw new Error('No active simulation')
      return await apiClient.post<SimulateTurnResponse>(
        `/api/workflows/simulations/${currentSession.instance_id}/events`,
        args,
      )
    },
    onSuccess: applyTurn,
  })

  const reset = useMutation<void, Error, void>({
    mutationFn: async () => {
      const currentSession = sessionRef.current
      if (!currentSession) return
      const response = await apiClient.fetch(
        `/api/workflows/simulations/${currentSession.instance_id}`,
        { method: 'DELETE' },
      )
      if (!response.ok && response.status !== 404) {
        throw new Error(`Reset failed (${response.status})`)
      }
    },
    onSuccess: () => {
      sessionRef.current = null
      setSession(null)
      setAgenda(null)
      setTranscript([])
      setStatus(null)
      setLastError(null)
    },
  })

  const state: SimulationState = useMemo(() => {
    if (start.isPending || sendMessage.isPending || fireEvent.isPending) {
      return 'running'
    }
    if (!session) return 'idle'
    if (status && TERMINAL_STATUSES.includes(status)) return 'terminal'
    if (agenda?.paused || status === 'paused') return 'paused'
    return 'ready'
  }, [start.isPending, sendMessage.isPending, fireEvent.isPending,
      session, status, agenda])

  return {
    start, sendMessage, fireEvent, reset,
    session, agenda, transcript, status,
    paused: agenda?.paused ?? null, lastError, state,
  }
}
